import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useComponentWebSocket } from './GlobalSettings/GlobalSettingUtils';

const AccountManagement = ({ userPermission, currentUsername }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    permission: 'view_only'
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Define command handlers for WebSocket messages
  const commandHandlers = {
    'get_all_users': (message) => {
      if (message.data.status === 'success') {
        setUsers(message.data.users || []);
      } else {
        showSnackbar(message.data.message || 'Failed to load users', 'error');
      }
      setLoading(false);
    },
    'add_user': (message) => {
      if (message.data.status === 'success') {
        showSnackbar(message.data.message || 'User added successfully', 'success');
        setAddUserOpen(false);
        setNewUser({ username: '', password: '', permission: 'view_only' });
        loadUsers();
      } else {
        showSnackbar(message.data.message || 'Failed to add user', 'error');
      }
      setLoading(false);
    },
    'delete_user': (message) => {
      if (message.data.status === 'success') {
        showSnackbar(message.data.message || 'User deleted successfully', 'success');
        setDeleteConfirmOpen(false);
        setUserToDelete(null);
        loadUsers();
      } else {
        showSnackbar(message.data.message || 'Failed to delete user', 'error');
      }
      setLoading(false);
    },
    'users_updated': (message) => {
      setUsers(message.data.users || []);
    }
  };

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  const loadUsers = useCallback(() => {
    if (userPermission !== 'admin') return;
    
    setLoading(true);
    sendCommand('get_all_users', {});
  }, [userPermission, sendCommand]);

  useEffect(() => {
    if (userPermission === 'admin') {
      loadUsers();
    }
  }, [userPermission, loadUsers]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleAddUser = () => {
    if (!newUser.username.trim() || !newUser.password.trim()) {
      showSnackbar('Username and password are required', 'error');
      return;
    }

    if (newUser.username.length < 3) {
      showSnackbar('Username must be at least 3 characters long', 'error');
      return;
    }

    if (newUser.password.length < 6) {
      showSnackbar('Password must be at least 6 characters long', 'error');
      return;
    }

    setLoading(true);
    sendCommand('add_user', {
      username: newUser.username.trim(),
      password: newUser.password,
      permission: newUser.permission
    });
  };

  const handleDeleteUser = (username) => {
    setUserToDelete(username);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = () => {
    if (!userToDelete) return;

    setLoading(true);
    sendCommand('delete_user', {
      username: userToDelete
    });
  };

  const getPermissionChip = (permission) => {
    const colors = {
      admin: 'error',
      editor: 'warning',
      view_only: 'default'
    };

    const labels = {
      admin: t('account_management.admin'),
      editor: t('account_management.editor'),
      view_only: t('account_management.view_only')
    };

    return (
      <Chip
        label={labels[permission] || permission}
        color={colors[permission] || 'default'}
        size="small"
      />
    );
  };

  if (userPermission !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('account_management.title')}
        </Typography>
        <Typography color="text.secondary">
          {t('account_management.admin_only')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">
          {t('account_management.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddUserOpen(true)}
          disabled={loading}
        >
          {t('account_management.add_user')}
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('account_management.username')}</TableCell>
              <TableCell>{t('account_management.permission')}</TableCell>
              <TableCell width={100}>{t('account_management.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  {user.username}
                  {user.username === currentUsername && (
                    <Chip label={t('account_management.you')} size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {getPermissionChip(user.permission)}
                    {Number(user.mfa_enabled) === 1 && (
                      <Chip
                        label={t('account_management.mfa_enabled')}
                        color="success"
                        size="small"
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteUser(user.username)}
                    disabled={user.username === currentUsername || loading}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('account_management.add_new_user')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3}>
            <TextField
              label={t('account_management.username')}
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              fullWidth
              autoFocus
            />
            <TextField
              label={t('account_management.password')}
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t('account_management.permission')}</InputLabel>
              <Select
                value={newUser.permission}
                onChange={(e) => setNewUser({ ...newUser, permission: e.target.value })}
                label={t('account_management.permission')}
              >
                <MenuItem value="view_only">{t('account_management.view_only')}</MenuItem>
                <MenuItem value="editor">{t('account_management.editor')}</MenuItem>
                <MenuItem value="admin">{t('account_management.admin')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)}>
            {t('account_management.cancel')}
          </Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={loading}
          >
            {t('account_management.add_user')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('account_management.confirm_delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('account_management.confirm_delete_message', { username: userToDelete })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {t('account_management.cancel')}
          </Button>
          <Button
            onClick={confirmDeleteUser}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {t('account_management.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountManagement;
